import Yup from '../yup';
import {constants} from '@neosvoc/nv-platform-core-common';
import _ from 'lodash';
import {getChoiceScriptValidatorSchema} from './logicValidatorSchemas';

const EPSILON = 1e-7;

const getDecimals = (number) => {
    if (!_.isNumber(number) || Math.floor(number.valueOf()) === number.valueOf()) {
        return 0;
    }
    return number.toString().split('.')[1].replace(/0+$/g, '').length;
};

const divide = (num, den) => {
    const rest = num % den;
    return rest < EPSILON || Math.abs(rest - den) < EPSILON;
};

const subtract = (minuend, subtrahend) => {
    return (minuend - subtrahend).toFixed(Math.max(getDecimals(minuend), getDecimals(subtrahend)));
};

const dateTransform = (curr, orig) => (orig === '' ? null : curr);

function DateRangeFn(value) {
    // eslint-disable-next-line no-invalid-this
    const {min, max} = this.parent;
    if (max != null && min != null) {
        return min <= max;
    }
    return true;
}


// to access context It cannot be an arrow function
function StepTestFn(step) {
    // eslint-disable-next-line no-invalid-this
    const {min, max} = this.parent;
    if (max != null && min != null) {
        return divide((max - min), step);
    }
    return true;
}

// to access context It cannot be an arrow function
function IntervalTestFn(interval) {
    // eslint-disable-next-line no-invalid-this
    const {min, max, step} = this.parent;
    if (max && min) {
        return divide((max - min), interval);
    }
    if (step) {
        return divide(interval, step);
    }
    return true;
}
function MinLteMaxMinusStepTestFn(minValue) {
    // eslint-disable-next-line no-invalid-this
    const {max, step} = this.parent;
    if (max == null || step == null || minValue == null) {
        return true;
    }
    if (minValue <= (max - step)) return true;
    // eslint-disable-next-line no-invalid-this
    return this.createError({
        message: {key: 'max', params: {max: max - step}},
    });
}
const typeValidatorSchemas = {
    [constants.EDITOR_TEMPLATES.STRING_1]: () => Yup.object().shape({
        id: Yup.string().required(),
        title: Yup.string().required(),
        subtitle: Yup.string(),
        required: Yup.boolean(),
    }),
    [constants.EDITOR_TEMPLATES.TEXT_1]: () => Yup.object().shape({
        text: Yup.string().required(),
        subtitle: Yup.string(),
        linkLabel: Yup.string(),
        linkUrl: Yup.string(),
        redirect: Yup.boolean(),
        redirectTimeout: Yup.number().min(0),
    }),
    [constants.EDITOR_TEMPLATES.NUMBER_1]: () => Yup.object().shape({
        id: Yup.string().required(),
        title: Yup.string().required(),
        subtitle: Yup.string(),
        required: Yup.boolean(),
        min: Yup.number(),
        max: Yup.number().when('min', (min) => {
            return (min === null)? Yup.number(): Yup.number().min(min);
        }),
        step: Yup.number().when(['max', 'min'], (max, min) => {
            if (max === null) return Yup.number().min(0);
            if (max === min) return Yup.number().nullable();
            return Yup.number().min(0).max(max).test('is-valid-step', () => ({key: 'invalidStep', params: {maxmin: subtract(max, min)}}), StepTestFn);
        }),
    }),
    [constants.EDITOR_TEMPLATES.NUMBER_2]: () => Yup.object().shape({
        id: Yup.string().required(),
        title: Yup.string().required(),
        subtitle: Yup.string(),
        required: Yup.boolean(),
        min: Yup.number().required().when(['componentType', 'nps'], (componentType, nps) => {
            if (componentType === 'rating') {
                const min = nps ? 0 : 1;
                return Yup.number().required().min(min).max(9);
            }
        }).test(
            'min-lte-max-minus-step',
            () => ({key: 'max'}),
            MinLteMaxMinusStepTestFn,
        ),
        max: Yup.number().when(['min', 'componentType'], (min, componentType) => {
            if (min === null) return Yup.number().required();

            if (componentType !== 'rating') {
                return Yup.number().required().min(min);
            }
            return Yup.number().required().min(1).max(10);
        }),
        minLabel: Yup.string(),
        maxLabel: Yup.string(),
        step: Yup.number().when(['max', 'min'], (max, min) => {
            return max === null ?
                Yup.number().required().min(0):
                Yup.number().required().min(0).max(max).test('is-valid-step', () => ({key: 'invalidStep', params: {maxmin: subtract(max, min)}}), StepTestFn);
        }),
        interval: Yup.number().when(['max', 'min', 'step'], (max, min, step) => {
            return max === null ?
                Yup.number().required().min(0):
                Yup.number().required().min(0).max(max).test('is-valid-interval',
                    () => ({key: 'invalidInterval', params: {maxmin: subtract(max, min), step}}), IntervalTestFn);
        }),
        componentType: Yup.string().oneOf(['slider', 'rating']),
        rangesColors: Yup.array().when('componentType', (componentType) => {
            if (componentType === 'rating') {
                return Yup.array().of(Yup.object().shape({
                    value: Yup.number().required().min(0).max(100),
                    color: Yup.string().required(),
                }));
            }
        }),
        exclusiveChoiceEnabled: Yup.boolean(),
        exclusiveChoice: Yup.object().when(['max', 'exclusiveChoiceEnabled'], (max, exclusiveChoiceEnabled) => {
            if (exclusiveChoiceEnabled) {
                return Yup.object().shape({
                    id: Yup.number().required().moreThan(max),
                    value: Yup.string().required(),
                });
            }
        }),
    }),
    [constants.EDITOR_TEMPLATES.MULTIPLE_NUMBER_1]: () => Yup.object().shape({
        id: Yup.string().required(),
        title: Yup.string().required(),
        subtitle: Yup.string(),
        required: Yup.boolean(),
        min: Yup.number().required(),
        max: Yup.number().when('min', (min) => {
            return min === null ? Yup.number().required() : Yup.number().required().min(min);
        }),
        step: Yup.number().when(['max', 'min'], (max, min) => {
            if (max === min) return Yup.number().nullable();
            return Yup.number().required().min(0).max(max)
                .test('is-valid-step', () => ({key: 'invalidStep', params: {maxmin: subtract(max, min)}}), StepTestFn);
        }),
        preset: Yup.number().required(),
        rows: Yup.array().of(
            Yup.object().shape({
                id: Yup.string().required(),
                minLabel: Yup.string().required(),
                maxLabel: Yup.string().required(),
            }),
        ).required().min(1, 'min'),
    }),
    [constants.EDITOR_TEMPLATES.MULTIPLE_NUMBER_2]: () => Yup.object().shape({
        id: Yup.string().required(),
        title: Yup.string().required(),
        subtitle: Yup.string(),
        required: Yup.boolean(),
        min: Yup.number().required().when('componentType', (componentType) => {
            if (componentType === 'rating') {
                return Yup.number().required().min(1).max(9);
            }
        }).test(
            'min-lte-max-minus-step',
            () => ({key: 'max'}),
            MinLteMaxMinusStepTestFn,
        ),
        max: Yup.number().when(['min', 'componentType'], (min, componentType) => {
            if (min === null) return Yup.number().required();

            if (componentType !== 'rating') {
                return Yup.number().required().min(min);
            }
            return Yup.number().required().min(1).max(10);
        }),
        minLabel: Yup.string(),
        maxLabel: Yup.string(),
        step: Yup.number().when(['max', 'min'], (max, min) => {
            return Yup.number().required().min(0).max(max)
                .test('is-valid-step', () => ({key: 'invalidStep', params: {maxmin: subtract(max, min)}}), StepTestFn);
        }),
        interval: Yup.number().when(['max', 'min', 'step'], (max, min, step) => {
            return Yup.number().required().min(0).max(max)
                .test('is-valid-interval', () => ({key: 'invalidInterval', params: {maxmin: subtract(max, min), step}}), IntervalTestFn);
        }),
        componentType: Yup.string().oneOf(['slider', 'rating']),
        rangesColors: Yup.array().when('componentType', (componentType) => {
            if (componentType === 'rating') {
                return Yup.array().of(Yup.object().shape({
                    value: Yup.number().required().min(0).max(100),
                    color: Yup.string().required(),
                }));
            }
        }),
        rows: Yup.array().of(
            Yup.object().shape({
                id: Yup.string().required(),
                label: Yup.string().required(),
                shuffle: Yup.boolean(),
            }),
        ).required().min(1, 'min'),
        exclusiveChoiceEnabled: Yup.boolean(),
        exclusiveChoice: Yup.object().when(['max', 'exclusiveChoiceEnabled'], (max, exclusiveChoiceEnabled) => {
            if (exclusiveChoiceEnabled) {
                return Yup.object().shape({
                    id: Yup.number().required().moreThan(max),
                    value: Yup.string().required(),
                });
            }
        }),
    }),
    [constants.EDITOR_TEMPLATES.MULTIPLE_NUMBER_3]: () => Yup.object().shape({
        id: Yup.string().required(),
        title: Yup.string().required(),
        subtitle: Yup.string(),
        required: Yup.boolean(),
        max: Yup.number().min(1).positive().required(),
        rows: Yup.array().of(
            Yup.object().shape({
                id: Yup.string().required(),
                label: Yup.string().required(),
                shuffle: Yup.boolean(),
            }),
        ).required().min(1, 'min'),
    }),
    [constants.EDITOR_TEMPLATES.SINGLE_CHOICE_1]: () => Yup.object().shape({
        id: Yup.string().required(),
        title: Yup.string().required(),
        subtitle: Yup.string(),
        required: Yup.boolean(),
        choices: Yup.array().of(
            Yup.object().shape({
                id: Yup.string().required(),
                value: Yup.string().required(),
                shuffle: Yup.boolean().required(),
                detail: Yup.boolean(),
            }),
        ).required().min(1, 'min'),
    }),
    [constants.EDITOR_TEMPLATES.SINGLE_CHOICE_2]: () => Yup.object().shape({
        id: Yup.string().required(),
        title: Yup.string().required(),
        subtitle: Yup.string(),
        required: Yup.boolean(),
        choices: Yup.array().of(
            Yup.object().shape({
                id: Yup.string().required(),
                value: Yup.string().required(),
                detail: Yup.boolean(),
            }),
        ).required().min(1, 'min'),
    }),
    [constants.EDITOR_TEMPLATES.SINGLE_CHOICE_3]: () => Yup.object().shape({
        id: Yup.string().required(),
        title: Yup.string().required(),
        subtitle: Yup.string(),
        required: Yup.boolean(),
        choices: Yup.array().of(
            Yup.object().shape({
                id: Yup.string().required(),
                value: Yup.string().required(),
            }),
        ).required().min(1, 'min'),
    }),
    [constants.EDITOR_TEMPLATES.SINGLE_CHOICE_4]: () => Yup.object().shape({
        id: Yup.string().required(),
        title: Yup.string().required(),
        subtitle: Yup.string(),
        required: Yup.boolean(),
        ratio: Yup.string(),
        choices: Yup.array().of(
            Yup.object().shape({
                id: Yup.string().required(),
                value: Yup.string().required(),
                url: Yup.string().url().required(),
                shuffle: Yup.boolean().default(false),
            }),
        ).required().min(1, 'min'),
    }),
    [constants.EDITOR_TEMPLATES.SINGLE_CHOICE_1_EXT]: () => Yup.object().shape({
        id: Yup.string().required(),
        title: Yup.string().required(),
        subtitle: Yup.string(),
        required: Yup.boolean(),
        url: Yup.string().url().required(),
    }),
    [constants.EDITOR_TEMPLATES.MULTIPLE_CHOICE_1]: () => Yup.object().shape({
        id: Yup.string().required(),
        title: Yup.string().required(),
        subtitle: Yup.string(),
        required: Yup.boolean(),
        choices: Yup.array().of(
            Yup.object().shape({
                id: Yup.string().required(),
                value: Yup.string().required(),
                shuffle: Yup.boolean().required(),
                detail: Yup.boolean(),
                exclusive: Yup.boolean(),
            }),
        ).required().min(1, 'min'),
        min: Yup.number().when('choices', (choices) => {
            const exclusiveChoices = choices.filter((c) => c.exclusive);
            return exclusiveChoices.length > 0 ? Yup.number().max(1, () => ({key: 'maxExclusive', params: {}})) : Yup.number().min(1).max(choices.length);
        }),
        max: Yup.number().when(['choices', 'min'], (choices, min) => {
            return min === null ? Yup.number().min(1).max(choices.length) : Yup.number().min(min).max(choices.length);
        }),
    }),
    [constants.EDITOR_TEMPLATES.MULTIPLE_CHOICE_2]: () => Yup.object().shape({
        id: Yup.string().required(),
        title: Yup.string().required(),
        subtitle: Yup.string(),
        required: Yup.boolean(),
        ratio: Yup.string(),
        choices: Yup.array().of(
            Yup.object().shape({
                id: Yup.string().required(),
                value: Yup.string().required(),
                url: Yup.string().required(),
                shuffle: Yup.boolean().required(),
                exclusive: Yup.boolean(),
            }),
        ).required().min(1, 'min'),
        min: Yup.number().when('choices', (choices) => {
            const exclusiveChoices = choices.filter((c) => c.exclusive);
            return exclusiveChoices.length > 0 ? Yup.number().max(1, () => ({key: 'maxExclusive', params: {}})) : Yup.number().min(1).max(choices.length);
        }),
        max: Yup.number().when(['choices', 'min'], (choices, min) => {
            return min === null ? Yup.number().min(1).max(choices.length) : Yup.number().min(min).max(choices.length);
        }),
    }),
    [constants.EDITOR_TEMPLATES.BATTERY_1]: (item) => Yup.object().shape({
        id: Yup.string().required(),
        title: Yup.string().required(),
        subtitle: Yup.string(),
        required: Yup.boolean(),
        choices: Yup.array().of(
            Yup.object().shape({
                id: Yup.string().required(),
                value: Yup.string().required(),
                shuffle: Yup.boolean().required(),
                occurrencies: Yup.number().min(1).max(item.rows.length),
            }),
        ).required().min(1, 'min'),
        rows: Yup.array().of(
            Yup.object().shape({
                id: Yup.string().required(),
                value: Yup.string().required(),
                shuffle: Yup.boolean().required(),
            }),
        ).required().min(1, 'min'),
    }),
    [constants.EDITOR_TEMPLATES.BATTERY_2]: (item) => {
        return Yup.object().shape({
            id: Yup.string().required(),
            title: Yup.string().required(),
            subtitle: Yup.string(),
            required: Yup.boolean(),
            choices: Yup.array().of(
                Yup.object().shape({
                    id: Yup.string().required(),
                    value: Yup.string().required(),
                    shuffle: Yup.boolean().required(),
                    min: Yup.number().min(1).max(item.rows.length),
                    occurrencies: Yup.number().min(1).max(item.rows.length),
                    exclusive: Yup.boolean(),
                }),
            ).required().min(1, 'min'),
            rows: Yup.array().of(
                Yup.object().shape({
                    id: Yup.string().required(),
                    value: Yup.string().required(),
                    shuffle: Yup.boolean().required(),
                    min: Yup.number().min(1).max(item.choices.length),
                    max: Yup.number().min(1).max(item.choices.length),
                }),
            ).required().min(1, 'min'),
            min: Yup.number(),
            max: Yup.number().when('min', (min) => {
                return min === null ? Yup.number() : Yup.number().min(min);
            }),
        });
    },
    [constants.EDITOR_TEMPLATES.DATE_1]: () => Yup.object().shape({
        id: Yup.string().required(),
        title: Yup.string().required(),
        subtitle: Yup.string(),
        required: Yup.boolean(),
        min: Yup.date().nullable().transform(dateTransform).test('is-valid-date-range',
            () => ({key: 'invalidDateRange'}), DateRangeFn),
        max: Yup.date().nullable().transform(dateTransform).test('is-valid-date-range',
            () => ({key: 'invalidDateRange'}), DateRangeFn),
    }),
    [constants.EDITOR_TEMPLATES.IMAGE_1]: () => Yup.object().shape({
        id: Yup.string().required(),
        title: Yup.string().required(),
        subtitle: Yup.string(),
        required: Yup.boolean(),
    }),
    [constants.EDITOR_TEMPLATES.GEO_1]: () => Yup.object().shape({
        id: Yup.string().required(),
        title: Yup.string().required(),
        subtitle: Yup.string(),
        required: Yup.boolean(),
    }),
    [constants.EDITOR_TEMPLATES.GENERATOR_CLASSIFIER]: () => Yup.object().shape({
        id: Yup.string().required(),
        title: Yup.string().required(),
        subtitle: Yup.string(),
        source: Yup.string().required(),
        required: Yup.boolean(),
        choices: Yup.array().of(
            Yup.object().shape({
                id: Yup.string().required(),
                value: Yup.string().required(),
                threshold: Yup.number().required(),
            }),
        ).required().min(1, 'min'),
    }),
    [constants.EDITOR_TEMPLATES.GENERATOR_CLASSIFIER_2]: (item, page) => Yup.object().shape({
        id: Yup.string().required(),
        title: Yup.string().required(),
        subtitle: Yup.string(),
        required: Yup.boolean(),
        choices: Yup.array().of(
            Yup.object().shape({
                id: Yup.string().required(),
                value: Yup.string().required(),
                choiceScripts: Yup.array().of(getChoiceScriptValidatorSchema(page.id)).required(),
            }),
        ).required().min(1, 'min')
            /* eslint-disable no-invalid-this */
            .test('are-choices-reachable', function(choices) {
                if (!_.isArray(choices) || choices.length === 0) {
                    return true;
                }

                const unreachableIndex = choices.findIndex((choice, index) => {
                    const scripts = choice.choiceScripts;
                    return (_.isArray(scripts) && scripts.length === 1 && _.isEmpty(scripts[0].expression)) &&
                        index !== choices.length - 1;
                });

                if (unreachableIndex !== -1) {
                    return this.createError({
                        path: `${this.path}[${unreachableIndex}].choiceScripts`,
                        message: {key: 'unreachableChoice'},
                    });
                }

                return true;
            })
            /* eslint-enable no-invalid-this */
        ,
    }),
    [constants.EDITOR_TEMPLATES.RANK_1]: (item) => Yup.object().shape({
        id: Yup.string().required(),
        title: Yup.string().required(),
        subtitle: Yup.string(),
        required: Yup.boolean(),
        rows: Yup.array().of(
            Yup.object().shape({
                id: Yup.string().required(),
                value: Yup.string().required(),
                shuffle: Yup.boolean().required(),
            }),
        ).required().min(1, 'min'),
        max: Yup.number()
            .transform((value) => Number.isNaN(value) ? null : value )
            .nullable(true)
            .min(1)
            .max(item.rows.length),
    }),
};

export default typeValidatorSchemas;
